import { EventEmitter } from 'events';

import { DealerSDK } from './DealerSDK.js';
import type { AudioAnalyser } from './audio.js';
import {
  SDK_EVENT_NAMES,
  type AgentState,
  type DealerVideoTrack,
  type SDKEventMap,
  type SDKEventName,
  type SDKServiceConfig,
  type SDKStateSnapshot,
} from './types.js';

export interface BaseSDKService {
  on<K extends SDKEventName>(event: K, listener: (payload: SDKEventMap[K]) => void): this;
  off<K extends SDKEventName>(event: K, listener: (payload: SDKEventMap[K]) => void): this;
  once<K extends SDKEventName>(event: K, listener: (payload: SDKEventMap[K]) => void): this;
  emit<K extends SDKEventName>(event: K, payload: SDKEventMap[K]): boolean;
  removeAllListeners<K extends SDKEventName>(event?: K): this;
}

/**
 * Shared base for every game-specific SDK service. Mirrors the iframe lib's
 * `BaseAIService` / `BlackjackAIService` / `RouletteAIService` inheritance:
 * composes a `DealerSDK`, forwards every event, and exposes the game-agnostic
 * surface (lifecycle, transport, getters, PTT primitives).
 *
 * Game subclasses add their validated `send*` methods and dispatch through
 * `protected dispatch(event)` — which doubles as the test seam for capturing
 * events instead of sending over RPC.
 */
export abstract class BaseSDKService extends EventEmitter {
  private readonly sdk: DealerSDK;

  constructor(config: SDKServiceConfig) {
    super();
    this.setMaxListeners(50);
    this.sdk = new DealerSDK(config);
    for (const name of SDK_EVENT_NAMES) {
      this.sdk.on(name, (payload) => this.emit(name, payload));
    }
  }

  // Lifecycle — mirrors BaseAIService
  initialize(): Promise<void> {
    return this.sdk.connect();
  }

  destroy(): void {
    this.sdk.destroy();
    this.removeAllListeners();
  }

  // Mute
  setDealerMute(muted: boolean): Promise<void> {
    return this.sdk.setDealerMute(muted);
  }

  // Audio
  startAudio(): Promise<void> {
    return this.sdk.startAudio();
  }

  setVolume(volume: number): void {
    this.sdk.setVolume(volume);
  }

  /** Dealer-voice analyser for host-driven visualisations; null until audio attaches. */
  getAudioAnalyser(options?: Parameters<DealerSDK['getAudioAnalyser']>[0]): AudioAnalyser | null {
    return this.sdk.getAudioAnalyser(options);
  }

  /** Local-mic analyser for the holding-state visualizer; null until first PTT hold. */
  getLocalAudioAnalyser(
    options?: Parameters<DealerSDK['getLocalAudioAnalyser']>[0]
  ): AudioAnalyser | null {
    return this.sdk.getLocalAudioAnalyser(options);
  }

  // State snapshot + convenience getters
  getVideoTrack(): DealerVideoTrack | null {
    return this.sdk.getVideoTrack();
  }

  getState(): SDKStateSnapshot {
    return this.sdk.getState();
  }

  getAgentState(): AgentState {
    return this.sdk.getState().agentState;
  }

  isReady(): boolean {
    return this.sdk.isReady();
  }

  // PTT primitives — gesture/swipe stays in consumer UI (Decision #11)
  acquireMic(): Promise<void> {
    return this.sdk.acquireMic();
  }

  releaseMic(): Promise<void> {
    return this.sdk.releaseMic();
  }

  openTurn(): Promise<void> {
    return this.sdk.openTurn();
  }

  startPTT(): Promise<void> {
    return this.sdk.startPTT();
  }

  endPTT(): Promise<void> {
    return this.sdk.endPTT();
  }

  cancelPTT(): Promise<void> {
    return this.sdk.cancelPTT();
  }

  /** Typed player message — enters the dialogue where speech enters (ADR 0065). */
  sendText(text: string): Promise<void> {
    return this.sdk.sendText(text);
  }

  /**
   * Test seam: subclasses override to capture events instead of dispatching
   * over RPC. Matches the iframe lib's `TestBlackjackService` / `TestRouletteService`
   * pattern in `__tests__/helpers/test-service-factory.ts`.
   */
  protected dispatch(event: { type: string; data?: unknown }): void {
    void this.sdk.sendGameEvent(event.type, event.data);
  }

  /**
   * Like dispatch(), but surfaces the RPC verdict/rejection to the caller —
   * for hosts that forward pre-validated raw envelopes and own the error
   * handling (the iframe app's game-event pass-through).
   */
  protected dispatchWithResult(event: {
    type: string;
    data?: unknown;
  }): Promise<{ success: boolean; error?: string }> {
    return this.sdk.sendGameEvent(event.type, event.data);
  }
}
